import { memo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuraLayout, Layout, PageTitle, AddCardIcon } from "lola-framework-ui-test";

import { FlowHeroMark } from "../../components/FlowHeroMark";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { DEFERRED_ORIGINATION_ROUTES, TITLE_COLOR } from "../../constants";
import { paymentType } from "../../interfaces/payment";
import { useStore } from "../../store/globalState";
import type { CardStepFields } from "./AddCardPage";

/**
 * Card registration transition (gallery-safe) for deferred funding cards.
 *
 * Opened from the Control Dock: static spinner, no redirects.
 * Reached via Add Card → Billing with router state: arms paymentState as a
 * **saved** instrument (register already happened in live) and returns to
 * Transfer after a short delay. Zero network.
 */
export const AddCardProcessingPage = memo(() => {
  const { t } = useTranslation("deferred-origination");
  const { theme, setPaymentState } = useStore();
  const navigate = useNavigate();
  const location = useLocation();

  const colorConfig = theme.whiteView;
  const { dropzoneColors, backgroundIcon } = colorConfig;

  useEffect(() => {
    const card = (location.state as { card?: CardStepFields } | null)?.card;
    if (!card) return;

    const digits = card.cardNumber.replace(/\D/g, "");
    const last4 = digits.slice(-4) || "4242";
    const brand = digits.startsWith("4") ? "VISA" : "MASTERCARD";

    setPaymentState({
      payment: {
        cardId: `pm_deferred_new_${last4}`,
        type: paymentType.CARD,
        brand: `${brand}-${last4}`,
      },
    });

    const timer = setTimeout(() => {
      navigate(DEFERRED_ORIGINATION_ROUTES.TRANSFER, { replace: true });
    }, 1500);

    return () => clearTimeout(timer);
  }, [location.state, navigate, setPaymentState]);

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark
            showSpinner
            dropzoneColors={dropzoneColors}
            backgroundIcon={backgroundIcon}
          >
            <AddCardIcon size={60} colors={dropzoneColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t("card.addingCard")}
              highlightColor={TITLE_COLOR}
              secudnary={t("card.pleaseWait")}
              secudnaryColor={TITLE_COLOR}
            />
          </section>
        </section>
      </Layout.Content>
    </AuraLayout>
  );
});
